import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { AppConstants, ENTRY_TYPES, LIMIT_TYPES } from 'src/app/utilities/constant';
import { ToastService } from '../../services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageSupportService } from 'src/app/services/language-support.service';
import { UserAuthService } from 'src/app/services/user-auth/user-auth.service';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { MachineAttribute } from 'src/app/pages/project-management/machine-details/machine-details-interface';
import { ModalController } from '@ionic/angular';
import { RemoveMachineModeComponent } from '../remove-machine-mode/remove-machine-mode.component';

// Interfaces for type safety
interface DataListItem {
  text: string;
  description: string;
}

interface LimitEntry {
  type: string;
  stringValue?: string;
  stringArrayValue?: string[];
  dataList?: string | DataListItem[];
}

interface LimitGroup {
  limitType: number;
  entries: LimitEntry[];
}

interface MachineLimitsApiResponse {
  flag: boolean;
  message: string;
  statusCode: number;
  response: MachineLimitsPayload;
}

interface MachineLimitsPayload {
  machineId: string;
  userId: string;
  is_limit_visible: boolean;
  limits: LimitGroup[];
  machineModeList: { id: string; mode_name: string }[];
}

@Component({
  selector: 'app-machine-limit-view',
  templateUrl: './machine-limit-view.component.html',
  styleUrls: ['./machine-limit-view.component.scss'],
})
export class MachineLimitViewComponent implements OnInit, OnChanges {
  public machineModeList = [];
  public lifeCyclePhaseList = [];
  public getMachineModesLoading: boolean;
  public machineLimitForm: FormGroup;
  public user: {};
  private userLanguage: any;
  @Input() public machineId: string;
  @Input() public initialReadOnlyMode: boolean = true;
  @Input() public isCloneMode: boolean = false;
  private machineLimitsLoaded = false;
  private originalIsLimitVisible = false;
  private userInfo: any;
  public isReadOnlyMode: boolean = true;
  private originalFormData: any = null;
  public attributesData: any;
  public roadmapWithHazards: any;
  public isLimitVisible: boolean = false; // Independent toggle property
  // Last non-empty machine mode selection, used to restore after an invalid clear in clone mode.
  private lastMachineModeIds: any[] = [];
  constructor(
    private backendService: BackendCallService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private translate: TranslateService,
    private serviceMulti: LanguageSupportService,
    private userAuthService: UserAuthService,
    public sharedValue: ShareValuesService,
    public modalController: ModalController,
    public cdr: ChangeDetectorRef
  ) {
    this.userInfo = this.userAuthService.getUserList();
    this.createForm();
    this.machineLimitForm.get('machineMode').valueChanges.subscribe((modes: any[]) => {
      if (Array.isArray(modes) && modes.length) {
        this.lastMachineModeIds = [...modes];
      }
    });
  }

  onToggleChange(event: any) { 
    this.isLimitVisible = event.detail.checked;
  }

  getMachineAttributes() {
      this.backendService.getMachinesAttributesData(this.machineId).subscribe((attributeDataResult: MachineAttribute) => {
        this.attributesData = attributeDataResult.value;
        this.attributesData.machineMetaData=attributeDataResult.value.machineMetaData;

        const currentFormModes = this.machineLimitForm?.get('machineMode')?.value || [];
        const formIsEmpty = !currentFormModes || currentFormModes.length === 0;
        
        if (formIsEmpty && this.attributesData?.machineModes && Array.isArray(this.attributesData.machineModes)) {
          const selectedModeIds = this.convertMachineIdsToFormValues(this.attributesData.machineModes);
          this.machineLimitForm.patchValue({
            machineMode: selectedModeIds
          });
        }
        
        console.log(this.attributesData,'in machine attributesData in limits');
      });
    }

  get selectedMachineModes(): string[] {
    // Always use IDs as the source of truth and map to current language names
    const storedIds = this.getStoredMachineModeIds();
    const allModes = this.sharedValue?.machineModeList || [];
    
    if (storedIds && storedIds.length > 0) {
      const currentLanguageNames = storedIds.map(id => {
        const mode = allModes.find(m => m.id === id);
        return mode?.name;
      }).filter(Boolean);
      return currentLanguageNames;
    }
    return [];
  }

  get selectedMachineModeIds(): string[] {
    const formModes = this.machineLimitForm?.get('machineMode')?.value || [];
    if (formModes && formModes.length > 0) {
      if (formModes[0]?.id) {
        return formModes.map(mode => mode.id);
      }
      return formModes;
    }
    const ids = this.attributesData?.machineModes || [];
    return ids;
  }

  /**
   * Get stored machine mode IDs from either form (as IDs) or attributesData
   * @returns Array of machine mode ID strings
   */
  private getStoredMachineModeIds(): string[] {
    // First check if form already stores IDs
    const formModes = this.machineLimitForm?.get('machineMode')?.value || [];
    if (this.isCloneMode) {
      return formModes.map(mode => String(mode?.id || mode));
    }
    
    // Check if form values are already IDs (UUIDs format)
    const isUuidFormat = (value: string) => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(value);
    
    if (formModes.length > 0 && formModes.every(isUuidFormat)) {
      return formModes;
    }
    
    // If form contains names, try to convert them to IDs
    if (formModes.length > 0) {
      const allModes = this.sharedValue?.machineModeList || [];
      const ids = formModes.map(name => {
        const mode = allModes.find(m => m.name === name || m.mode_name === name);
        return mode?.id;
      }).filter(Boolean);
      return ids;
    }
    // Fallback to attributesData
    const attributeIds = this.attributesData?.machineModes || [];
    return attributeIds.map(id => id.toString());
  }

  /**
   * Convert machine mode IDs to IDs for form population (store IDs in form)
   * @param selectedIds - Array of selected machine mode IDs
   * @returns Array of machine mode IDs (strings)
   */
  private convertMachineIdsToFormValues(selectedIds: number[]): string[] {
    if (!selectedIds || !Array.isArray(selectedIds)) {
      return [];
    }
    
    // Store IDs in the form instead of names to avoid translation issues
    return selectedIds.map(id => id.toString());
  }

  /**
   * Convert machine mode IDs to objects with id and mode_name for payload
   * @param selectedIds - Array of selected machine mode IDs
   * @returns Array of objects with id and mode_name
   */
  private convertMachineIdsToPayloadObjects(selectedIds: string[]): { id: string; mode_name: string }[] {
    if (!selectedIds || !Array.isArray(selectedIds)) {
      return [];
    }
    
    const allModes = this.sharedValue?.machineModeList || [];
    
    const result = selectedIds.map(id => {
      const mode = allModes.find(mode => String(mode.id) === String(id));
      if (mode) {
        const mappedObject = { id: mode.id.toString(), mode_name: mode.mode_name };
        return mappedObject;
      } else {
        return null;
      }
    }).filter(Boolean) as { id: string; mode_name: string }[];
    return result;
  }

  public ngOnInit() {
    this.userLanguage = this.serviceMulti.getDefaultLanguage();
    this.applyCloneReadOnlyMode();
    // Subscribe to language changes to update form values when language changes
    this.translate.onLangChange.subscribe((event: any) => {
      this.userLanguage = event.lang;
    });
    
    this.getMachineAttributes();
    this.getMachineLimits();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isCloneMode || changes.initialReadOnlyMode) {
      this.applyCloneReadOnlyMode();
    }
    if ((changes.isCloneMode && !changes.isCloneMode.firstChange && !this.isCloneMode)
      || (changes.machineId && !changes.machineId.firstChange && changes.machineId.previousValue !== this.machineId)) {
      this.attributesData = null;
      this.machineLimitForm.reset();
      this.isLimitVisible = false;
      this.originalFormData = null;
      this.getMachineAttributes();
      this.getMachineLimits();
    }
  }

  private applyCloneReadOnlyMode(): void {
    this.isReadOnlyMode = this.isCloneMode ? false : this.initialReadOnlyMode;
    const machineMode = this.machineLimitForm?.get('machineMode');
    machineMode?.setValidators(this.isCloneMode ? [] : [Validators.required, this.arrayMinLengthValidator(1)]);
    machineMode?.updateValueAndValidity({ emitEvent: false });
  }

  private createForm(): void {
    this.machineLimitForm = this.formBuilder.group({
      is_limit_visible: [false],
      machineMode: [[], [Validators.required, this.arrayMinLengthValidator(1)]],
      useOfMachinery: ['', [this.characterLimitValidator(2000)]],
      userRoles: this.formBuilder.array([
        this.createUserRoleGroup()
      ]),
      exposure: ['', [this.characterLimitValidator(2000)]],
      numberOfOperators: ['', [Validators.pattern(/^[0-9]+$/), Validators.maxLength(4)]],
      otherUseLimit: ['', [this.characterLimitValidator(2000)]],
      machineSize: ['', [this.characterLimitValidator(150)]],
      humanInteraction: ['', [this.characterLimitValidator(2000)]],
      powerSupplyInterface: ['', [this.characterLimitValidator(2000)]],
      otherSpaceLimit: ['', [this.characterLimitValidator(2000)]],
      lifeOfMachinery: ['', [this.characterLimitValidator(2000)]],
      otherTimeLimit: ['', [this.characterLimitValidator(2000)]],
      recommendedServiceInterval: ['', [this.characterLimitValidator(2000)]],
      propertiesOfMaterialsToProcess: ['', [this.characterLimitValidator(2000)]],
      levelOfCleanliness: ['', [this.characterLimitValidator(2000)]],
      environmentalConditions: ['', [this.characterLimitValidator(2000)]],
      other: ['', [this.characterLimitValidator(2000)]],
      otherLimits: this.formBuilder.array([
        this.createOtherLimitGroup()
      ]),
      phaseOfMachineLifeCycle: ['', [this.characterLimitValidator(2000)]]
    });
  }

  // Create a single user role form group
  private createUserRoleGroup(): FormGroup {
    return this.formBuilder.group({
      userRole: ['', [this.characterLimitValidator(150)]],
      userDetails: ['', [this.characterLimitValidator(2000)]]
    });
  }

  // Custom validator for character limit (warning, not error)
  private characterLimitValidator(maxLength: number) {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value && control.value.length > maxLength) {
        return { 'characterLimitExceeded': { actualLength: control.value.length, maxLength: maxLength } };
      }
      return null;
    };
  }

  // Custom validator for arrays to ensure at least one item is selected
  private arrayMinLengthValidator(minLength: number) {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value && Array.isArray(control.value) && control.value.length >= minLength) {
        return null;
      }
      return { 'arrayMinLength': { actualLength: control.value ? control.value.length : 0, minLength: minLength } };
    };
  }

  // Getter methods for easy access to form controls in template
  public get is_limit_visible() { return this.machineLimitForm.get('is_limit_visible'); }
  public get machineMode() { return this.machineLimitForm.get('machineMode'); }
  public get useOfMachinery() { return this.machineLimitForm.get('useOfMachinery'); }
  public get exposure() { return this.machineLimitForm.get('exposure'); }
  public get userRoles() { return this.machineLimitForm.get('userRoles') as FormArray; }
  public get numberOfOperators() { return this.machineLimitForm.get('numberOfOperators'); }
  public get otherUseLimit() { return this.machineLimitForm.get('otherUseLimit'); }
  public get machineSize() { return this.machineLimitForm.get('machineSize'); }
  public get humanInteraction() { return this.machineLimitForm.get('humanInteraction'); }
  public get powerSupplyInterface() { return this.machineLimitForm.get('powerSupplyInterface'); }
  public get otherSpaceLimit() { return this.machineLimitForm.get('otherSpaceLimit'); }
  public get lifeOfMachinery() { return this.machineLimitForm.get('lifeOfMachinery'); }
  public get otherTimeLimit() { return this.machineLimitForm.get('otherTimeLimit'); }
  public get recommendedServiceInterval() { return this.machineLimitForm.get('recommendedServiceInterval'); }
  public get propertiesOfMaterialsToProcess() { return this.machineLimitForm.get('propertiesOfMaterialsToProcess'); }
  public get levelOfCleanliness() { return this.machineLimitForm.get('levelOfCleanliness'); }
  public get environmentalConditions() { return this.machineLimitForm.get('environmentalConditions'); }
  public get other() { return this.machineLimitForm.get('other'); }
  public get phaseOfMachineLifeCycle() { return this.machineLimitForm.get('phaseOfMachineLifeCycle'); }
  // Get specific user role group
  public getUserRoleGroup(index: number): FormGroup {
    return this.userRoles.at(index) as FormGroup;
  }

  // Get specific user role control
  public getUserRoleControl(index: number, controlName: string): AbstractControl | null {
    return this.getUserRoleGroup(index).get(controlName);
  }

  // Add a new user role
  public addNewUserRole(): void {
    const newUserRole = this.createUserRoleGroup();
    this.userRoles.push(newUserRole);
  }

  // Delete a user role by index
  public deleteUserRole(index: number): void {
    if (this.userRoles.length > 1) { // Keep at least one user role
      this.userRoles.removeAt(index);
    }
  }

  // Check if user role can be deleted (not the last one)
  public canDeleteUserRole(index: number): boolean {
    return this.userRoles.length > 1;
  }

  // Helper method to check if a field should show validation errors
  public shouldShowError(fieldName: string): boolean {
    const field = this.machineLimitForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  // Helper method to check if a user role field has a character limit warning
  public hasUserRoleCharacterLimitWarning(index: number, fieldName: string): boolean {
    const field = this.getUserRoleControl(index, fieldName);
    return field ? field.hasError('characterLimitExceeded') : false;
  }

  // Helper method to get character count for future use
  public getCharacterCount(fieldName: string): number {
    const field = this.machineLimitForm.get(fieldName);
    return field?.value ? field.value.length : 0;
  }

  // Helper method to get character count for user role field
  public getUserRoleCharacterCount(index: number, fieldName: string): number {
    const field = this.getUserRoleControl(index, fieldName);
    return field?.value ? field.value.length : 0;
  }

  // Helper method to get max character limit for a field
  public getMaxCharacterLimit(fieldName: string): number {
    // You can extend this to get limits from form validators or configuration
    const characterLimits: { [key: string]: number } = {
      'useOfMachinery': 2000,
      'userDetails': 2000,
      'exposure': 2000
    };
    return characterLimits[fieldName] || 0;
  }

  // Helper method to format character count display (for future use)
  public getCharacterCountDisplay(fieldName: string): string {
    const current = this.getCharacterCount(fieldName);
    const max = this.getMaxCharacterLimit(fieldName);
    if (max > 0) {
      return `${current}/${max}`;
    }
    return `${current}`;
  }

  // Helper method to determine character count status (for future styling)
  public getCharacterCountStatus(fieldName: string): 'normal' | 'warning' | 'error' {
    const current = this.getCharacterCount(fieldName);
    const max = this.getMaxCharacterLimit(fieldName);

    if (max === 0) return 'normal';

    const percentage = (current / max) * 100;
    if (percentage > 100) return 'error';
    if (percentage > 80) return 'warning';
    return 'normal';
  }

  // Helper method to get character limit warning message
  public getCharacterLimitMessage(fieldName: string): string {
    const field = this.machineLimitForm.get(fieldName);
    if (field && field.hasError('characterLimitExceeded')) {
      const error = field.getError('characterLimitExceeded');
      const LimitText = this.translate.instant('character_limit_exceeded');

      return `${LimitText} (${error.actualLength}/${error.maxLength})`;
    }
    return '';
  }

  // Helper method to get character limit warning message for user role
  public getUserRoleCharacterLimitMessage(index: number, fieldName: string): string {
    const field = this.getUserRoleControl(index, fieldName);
    if (field && field.hasError('characterLimitExceeded')) {
      const error = field.getError('characterLimitExceeded');
      const LimitText = this.translate.instant('character_limit_exceeded');
      return `${LimitText} (${error.actualLength}/${error.maxLength})`;
    }
    return '';
  }

  // Input restriction methods to block typing beyond limits
  public onInputRestriction(event: any, maxLength: number): void {
    const target = event.target;
    if (target.value && target.value.length >= maxLength) {
      event.preventDefault();
      target.value = target.value.substring(0, maxLength);
    }
  }

  public onNumberInputRestriction(event: any): void {
    const target = event.target;
    // Remove any non-numeric characters
    let value = target.value.replace(/[^0-9]/g, '');
    // Limit to 4 digits
    if (value.length > 4) {
      value = value.substring(0, 4);
    }
    target.value = value;
  }

  public saveMachineLimits() {
    if (this.isCloneMode) {
      return;
    }
    this.machineLimitForm.markAllAsTouched();
    const validationErrors = this.getValidationErrors();
    if (validationErrors.length > 0) {
      const errorMessage = this.translate.instant('one_or_more_fields_missing_validation');
      this.toastService.presentToast(errorMessage, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
      return;
    }

    const formData = this.machineLimitForm.value;
    const payload = this.createMachineLimitsPayload(formData);

    this.logCharacterLimitWarnings();
    this.backendService.saveMachineLimits(payload).subscribe(
      (response: any) => {
        if (response.statusCode === 200) {
          this.toastService.presentToast(
            this.translate.instant('customer_management_updated_successfully', { entity: this.translate.instant('Machine Limits') }),
            4000,
            AppConstants.SUCCESS_SNACKBAR,
            AppConstants.TOP
          );
          // Update attributesData with current form IDs before switching to read-only mode
          const currentFormModeIds = this.getStoredMachineModeIds();
          if (currentFormModeIds.length > 0) {
            if (!this.attributesData) {
              this.attributesData = {};
            }
            this.attributesData.machineModes = currentFormModeIds;
          }
          // Switch to read-only mode
          this.enableReadOnlyMode();
        }
      },
      (error) => {
        this.toastService.presentToast(error.error?.message, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP, 'error_occurred');
      }
    );
  }


  /**
   * Creates machine limits payload for API submission
   * @param formData - The form data containing all machine limit values
   * @returns The formatted payload object for the API
   */
  private createMachineLimitsPayload(formData: any): MachineLimitsPayload {
    // Get the actual stored IDs from the form
    const storedIds = this.getStoredMachineModeIds();
    const payload: MachineLimitsPayload = {
      machineId: this.machineId,
      userId: this.userInfo?.userId,
      is_limit_visible: this.isLimitVisible,
      limits: [],
      machineModeList: this.convertMachineIdsToPayloadObjects(storedIds)
    };
    const useLimits = this.createUseLimits(formData);
    const spaceLimits = this.createSpaceLimits(formData);
    const timeLimits = this.createTimeLimits(formData);
    const otherLimits = this.createOtherLimits(formData);

    payload.limits.push(useLimits, spaceLimits, timeLimits, otherLimits);
    return payload;
  }

  public getCloneMachineLimitsPayload(): any {
    if (!this.machineLimitForm || (!this.machineLimitsLoaded && !this.machineLimitForm.dirty)) {
      return null;
    }

    const payload = this.createMachineLimitsPayload(this.machineLimitForm.value);
    payload.machineId = '';
    return payload;
  }

  public hasCloneMachineLimitsEdits(): boolean {
    return !!this.machineLimitForm
      && (this.machineLimitForm.dirty || this.hasUnsavedChanges() || this.isLimitVisible !== this.originalIsLimitVisible);
  }

  /**
   * Creates use limits group (limit type 1)
   * 
   * @param formData - The form data
   * @returns LimitGroup for use limits
   */
  private createUseLimits(formData: any): LimitGroup {
    const useLimits: LimitGroup = {
      limitType: LIMIT_TYPES.USE_LIMITS,
      entries: []
    };

    // Convert IDs back to mode_names (English) for backend storage
    const machineMode = formData.machineMode || [];
    const allModes = this.sharedValue?.machineModeList || [];
    const modeNames = machineMode.map(id => {
      const mode = allModes.find(m => m.id === id);
      // Use mode_name (English) instead of name (localized) for consistent backend storage
      return mode?.mode_name;
    }).filter(Boolean);
    

    useLimits.entries.push({
      type: ENTRY_TYPES.MACHINE_MODES,
      stringArrayValue: modeNames
    });

    useLimits.entries.push({
      type: ENTRY_TYPES.USE_OF_MACHINERY,
      stringValue: formData.useOfMachinery || ''
    });

    useLimits.entries.push({
      type: ENTRY_TYPES.EXPOSURE_OF_OTHER_PERSON,
      stringValue: formData.exposure || ''
    });

    useLimits.entries.push({
      type: ENTRY_TYPES.NUMBER_OF_OPERATORS,
      stringValue: formData.numberOfOperators || ''
    });

    useLimits.entries.push({
      type: ENTRY_TYPES.OTHER_USER_LIMIT,
      stringValue: formData.otherUseLimit || ''
    });

    const userRolesData: DataListItem[] = (formData.userRoles || []).map((role: any) => ({
      text: role.userRole || '',
      description: role.userDetails || ''
    }));

    useLimits.entries.push({
      type: ENTRY_TYPES.USER_ROLES,
      dataList: userRolesData
    });

    return useLimits;
  }

  /**
   * Creates space limits group (limit type 2)
   * 
   * @param formData - The form data
   * @returns LimitGroup for space limits
   */
  private createSpaceLimits(formData: any): LimitGroup {
    const spaceLimits: LimitGroup = {
      limitType: LIMIT_TYPES.SPACE_LIMITS,
      entries: []
    };

    spaceLimits.entries.push({
      type: ENTRY_TYPES.MACHINE_SIZE,
      stringValue: formData.machineSize || ''
    });

    spaceLimits.entries.push({
      type: ENTRY_TYPES.HUMAN_INTERACTION,
      stringValue: formData.humanInteraction || ''
    });

    spaceLimits.entries.push({
      type: ENTRY_TYPES.POWER_SUPPLY_INTERFACE,
      stringValue: formData.powerSupplyInterface || ''
    });

    spaceLimits.entries.push({
      type: ENTRY_TYPES.OTHER_SPACE_LIMIT,
      stringValue: formData.otherSpaceLimit || ''
    });

    return spaceLimits;
  }

  /**
   * Creates time limits group (limit type 3)
   * @param formData - The form data
   * @returns LimitGroup for time limits
   */
  private createTimeLimits(formData: any): LimitGroup {
    const timeLimits: LimitGroup = {
      limitType: LIMIT_TYPES.TIME_LIMITS,
      entries: []
    };

    timeLimits.entries.push({
      type: ENTRY_TYPES.LIFE_OF_MACHINERY,
      stringValue: formData.lifeOfMachinery || ''
    });

    timeLimits.entries.push({
      type: ENTRY_TYPES.OTHER_TIME_LIMIT,
      stringValue: formData.otherTimeLimit || ''
    });

    timeLimits.entries.push({
      type: ENTRY_TYPES.RECOMMENDED_SERVICE_INTERVALS,
      stringValue: formData.recommendedServiceInterval || ''
    });

    return timeLimits;
  }

  /**
   * Creates other limits group (limit type 4)
   * 
   * @param formData - The form data
   * @returns LimitGroup for other limits
   */
  private createOtherLimits(formData: any): LimitGroup {
    const otherLimits: LimitGroup = {
      limitType: LIMIT_TYPES.OTHER_LIMITS,
      entries: []
    };

    otherLimits.entries.push({
      type: ENTRY_TYPES.PROPERTIES_OF_MATERIAL_TO_BE_PROCESSED,
      stringValue: formData.propertiesOfMaterialsToProcess || ''
    });

    otherLimits.entries.push({
      type: ENTRY_TYPES.LEVEL_OF_CLEANLINESS,
      stringValue: formData.levelOfCleanliness || ''
    });

    otherLimits.entries.push({
      type: ENTRY_TYPES.ENVIRONMENTAL_CONDITIONS,
      stringValue: formData.environmentalConditions || ''
    });

    otherLimits.entries.push({
      type: ENTRY_TYPES.OTHER,
      stringValue: formData.other || ''
    });

    otherLimits.entries.push({
      type: ENTRY_TYPES.PHASES_OF_MACHINE_LIFE_CYCLE,
      stringValue: formData.phaseOfMachineLifeCycle || ''
    });

    const otherLimitsData: DataListItem[] = (formData.otherLimits || []).map((limit: any) => ({
      text: limit.otherLimitFieldName || '',
      description: limit.description || ''
    }));

    otherLimits.entries.push({
      type: ENTRY_TYPES.OTHER_LIMIT_FIELD_NAME_AND_DESCRIPTION,
      dataList: otherLimitsData
    });

    return otherLimits;
  }

  // Helper method to check if form has required field errors (excluding character limit warnings)
  private hasRequiredFieldErrors(): boolean {
    const requiredFields = ['machineMode', 'useOfMachinery'];
    return requiredFields.some(fieldName => {
      const field = this.machineLimitForm.get(fieldName);
      return field?.hasError('required') || false;
    });
  }

  // Helper method to log character limit warnings
  private logCharacterLimitWarnings(): void {
    if (this.hasCharacterLimitWarning('useOfMachinery')) {
      console.warn(`Warning: useOfMachinery exceeds character limit - ${this.getCharacterLimitMessage('useOfMachinery')}`);
    }

    for (let i = 0; i < this.userRoles.length; i++) {
      if (this.hasUserRoleCharacterLimitWarning(i, 'userDetails')) {
        console.warn(`Warning: User role ${i + 1} userDetails exceeds character limit - ${this.getUserRoleCharacterLimitMessage(i, 'userDetails')}`);
      }
    }
  }

  public cancel() {
    // Restore original form data if it exists
    if (this.originalFormData) {
      this.machineLimitForm.patchValue(this.originalFormData);

      // Restore user roles array
      const userRolesArray = this.machineLimitForm.get('userRoles') as FormArray;
      userRolesArray.clear();
      if (this.originalFormData.userRoles && this.originalFormData.userRoles.length > 0) {
        this.originalFormData.userRoles.forEach((userRole: any) => {
          userRolesArray.push(this.formBuilder.group({
            userRole: [userRole.userRole || '', [this.characterLimitValidator(150)]],
            userDetails: [userRole.userDetails || '', [this.characterLimitValidator(2000)]]
          }));
        });
      } else {
        // Add at least one empty user role if none exist
        userRolesArray.push(this.createUserRoleGroup());
      }

      // Restore other limits array
      const otherLimitsArray = this.machineLimitForm.get('otherLimits') as FormArray;
      otherLimitsArray.clear();
      if (this.originalFormData.otherLimits && this.originalFormData.otherLimits.length > 0) {
        this.originalFormData.otherLimits.forEach((otherLimit: any) => {
          otherLimitsArray.push(this.formBuilder.group({
            otherLimitFieldName: [otherLimit.otherLimitFieldName || '', [this.characterLimitValidator(150)]],
            description: [otherLimit.description || '', [this.characterLimitValidator(2000)]]
          }));
        });
      } else {
        // Add at least one empty other limit if none exist
        otherLimitsArray.push(this.createOtherLimitGroup());
      }
    }

    // Clear any validation errors by resetting the form state
    this.machineLimitForm.markAsUntouched();
    this.machineLimitForm.markAsPristine();

    // Switch back to read-only mode
    this.enableReadOnlyMode();
  }

  public getMachineModesList() {
    this.getMachineModesLoading = true;
    this.backendService.getMachineLimitsByServiceMachineId(this.machineId, this.userLanguage)
      .subscribe(
        (data: any) => {
          this.machineModeList = data.response;
          this.getMachineModesLoading = false;
        },
        (err) => {
          this.getMachineModesLoading = false;
        }
      );
  }

  // Create a single other limit form group
  private createOtherLimitGroup(): FormGroup {
    return this.formBuilder.group({
      otherLimitFieldName: ['', [this.characterLimitValidator(150)]],
      description: ['', [this.characterLimitValidator(2000)]]
    });
  }

  // Add getter method:
  public get otherLimits() { return this.machineLimitForm.get('otherLimits') as FormArray; }

  // Add these methods for other limits management:
  // Get specific other limit group
  public getOtherLimitGroup(index: number): FormGroup {
    return this.otherLimits.at(index) as FormGroup;
  }

  // Get specific other limit control
  public getOtherLimitControl(index: number, controlName: string): AbstractControl | null {
    return this.getOtherLimitGroup(index).get(controlName);
  }

  // Add a new other limit
  public addNewOtherLimit(): void {
    const newOtherLimit = this.createOtherLimitGroup();
    this.otherLimits.push(newOtherLimit);
  }

  // Delete an other limit by index
  public deleteOtherLimit(index: number): void {
    if (this.otherLimits.length > 1) { // Keep at least one other limit
      this.otherLimits.removeAt(index);
      console.log(`Other limit at index ${index} deleted. Remaining limits:`, this.otherLimits.length);
    } else {
      console.log('Cannot delete the last other limit');
    }
  }

  // Check if other limit can be deleted (not the last one)
  public canDeleteOtherLimit(index: number): boolean {
    return this.otherLimits.length > 1;
  }

  // Helper method to check if an other limit field should show validation errors
  public shouldShowOtherLimitError(index: number, fieldName: string): boolean {
    const field = this.getOtherLimitControl(index, fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  // Helper method to check if an other limit field has a character limit warning
  public hasOtherLimitCharacterLimitWarning(index: number, fieldName: string): boolean {
    const field = this.getOtherLimitControl(index, fieldName);
    return field ? field.hasError('characterLimitExceeded') : false;
  }

  // Helper method to get character count for other limit field
  public getOtherLimitCharacterCount(index: number, fieldName: string): number {
    const field = this.getOtherLimitControl(index, fieldName);
    return field?.value ? field.value.length : 0;
  }

  // Helper method to get character limit warning message for other limit
  public getOtherLimitCharacterLimitMessage(index: number, fieldName: string): string {
    const field = this.getOtherLimitControl(index, fieldName);
    if (field && field.hasError('characterLimitExceeded')) {
      const error = field.getError('characterLimitExceeded');
      const LimitText = this.translate.instant('character_limit_exceeded');
      return `${LimitText} (${error.actualLength}/${error.maxLength})`;
    }
    return '';
  }

  // Helper method to check if a field has a character limit warning
  public hasCharacterLimitWarning(fieldName: string): boolean {
    const field = this.machineLimitForm.get(fieldName);
    return field ? field.hasError('characterLimitExceeded') : false;
  }

  // Helper method to get character status for other limit fields
  public getOtherLimitCharacterStatus(index: number, fieldName: string): 'normal' | 'warning' | 'error' {
    const current = this.getOtherLimitCharacterCount(index, fieldName);
    const max = this.getMaxCharacterLimit(fieldName);

    if (max === 0) return 'normal';

    const percentage = (current / max) * 100;
    if (percentage > 100) return 'error';
    if (percentage > 80) return 'warning';
    return 'normal';
  }

  // Helper method to get all validation errors that should block saving
  private getValidationErrors(): string[] {
    const errors: string[] = [];

    // Check required fields - only machineMode is required
    if (this.machineMode?.hasError('required') || this.machineMode?.hasError('arrayMinLength')) {
      const errorText = this.translate.instant('machine_mode_required');
      errors.push(errorText);
    }

    // Check numberOfOperators validation (only if user entered something)
    if (this.numberOfOperators?.value && this.numberOfOperators?.hasError('pattern')) {
      const errorText = this.translate.instant('only_numbers_allowed_msg');
      errors.push(`Number of Operators: ${errorText}`);
    }
    if (this.numberOfOperators?.value && this.numberOfOperators?.hasError('maxlength')) {
      const errorText = this.translate.instant('maximum_4_digits_allowed');
      errors.push(`Number of Operators: ${errorText}`);
    }

    // Check machineSize validation (only if user entered something)
    if (this.machineSize?.value && this.machineSize?.hasError('characterLimitExceeded')) {
      const error = this.machineSize?.getError('characterLimitExceeded');
      const LimitText = this.translate.instant('character_limit_exceeded');
      errors.push(`Machine Size: ${LimitText} (${error.actualLength}/${error.maxLength})`);
    }

    // Check userRole fields - only if user entered something (multiple entries)
    for (let i = 0; i < this.userRoles.length; i++) {
      const userRoleControl = this.getUserRoleControl(i, 'userRole');
      if (userRoleControl?.value && userRoleControl?.hasError('characterLimitExceeded')) {
        const error = userRoleControl.getError('characterLimitExceeded');
        const LimitText = this.translate.instant('character_limit_exceeded');
        errors.push(`User Role ${i + 1}: ${LimitText} (${error.actualLength}/${error.maxLength})`);
      }
    }

    // Check otherLimitFieldName fields - only if user entered something (multiple entries)
    for (let i = 0; i < this.otherLimits.length; i++) {
      const otherLimitFieldControl = this.getOtherLimitControl(i, 'otherLimitFieldName');
      if (otherLimitFieldControl?.value && otherLimitFieldControl?.hasError('characterLimitExceeded')) {
        const error = otherLimitFieldControl.getError('characterLimitExceeded');
        const LimitText = this.translate.instant('character_limit_exceeded');
        errors.push(`Other Limit Field Name ${i + 1}: ${LimitText} (${error.actualLength}/${error.maxLength})`);
      }
    }

    return errors;
  }

  // Method to enable edit mode
  public enableEditMode(): void {
    if (this.isCloneMode) {
      return;
    }
    // Store a deep copy of the current form data before entering edit mode
    this.originalFormData = JSON.parse(JSON.stringify(this.machineLimitForm.value));
    this.isReadOnlyMode = false;
  }

  // Method to enable read-only mode (typically called after save)
  public enableReadOnlyMode(): void {
    if (this.isCloneMode) {
      this.applyCloneReadOnlyMode();
      return;
    }
    this.isReadOnlyMode = true;
  }

  // Helper method to get user role value for read-only display
  public getUserRoleValue(index: number, fieldName: string): string {
    const userRoleGroup = this.getUserRoleGroup(index);
    return userRoleGroup?.get(fieldName)?.value || '';
  }

  // Helper method to get other limit value for read-only display
  public getOtherLimitValue(index: number, fieldName: string): string {
    const otherLimitGroup = this.getOtherLimitGroup(index);
    return otherLimitGroup?.get(fieldName)?.value || '';
  }

  // Method to set the baseline data for cancellation (call this after loading initial data)
  public setBaselineData(): void {
    this.originalFormData = JSON.parse(JSON.stringify(this.machineLimitForm.value));
  }

  // Method to check if form has unsaved changes
  public hasUnsavedChanges(): boolean {
    if (!this.originalFormData) {
      return false;
    }
    const currentData = JSON.stringify(this.machineLimitForm.value);
    const originalData = JSON.stringify(this.originalFormData);
    return currentData !== originalData;
  }

  // Method to load machine limits from API response
  public loadMachineLimits(apiResponse: MachineLimitsPayload): void {
    if (!apiResponse || !apiResponse.limits) {
      console.warn('Invalid API response structure');
      return;
    }
    // Reset form to initial state
    this.machineLimitForm.reset();
    this.isLimitVisible = apiResponse.is_limit_visible || false;
    this.cdr.detectChanges();
    // Clear form arrays
    const userRolesArray = this.machineLimitForm.get('userRoles') as FormArray;
    const otherLimitsArray = this.machineLimitForm.get('otherLimits') as FormArray;
    userRolesArray.clear();
    otherLimitsArray.clear();

    // Process each limit group
    apiResponse.limits.forEach(limitGroup => {
      this.processLimitGroup(limitGroup);
    });

    // Update attributesData with machine mode IDs for selectedMachineModes getter
    const currentFormModeIds = this.machineLimitForm?.get('machineMode')?.value || [];
    if (currentFormModeIds.length > 0) {
      // Ensure attributesData exists and update machine modes
      if (!this.attributesData) {
        this.attributesData = {};
      }
      this.attributesData.machineModes = currentFormModeIds;
    }

    // Ensure at least one user role and other limit exists
    if (userRolesArray.length === 0) {
      userRolesArray.push(this.createUserRoleGroup());
    }
    if (otherLimitsArray.length === 0) {
      otherLimitsArray.push(this.createOtherLimitGroup());
    }

    // Set baseline data and switch to read-only mode
    this.setBaselineData();
    this.originalIsLimitVisible = this.isLimitVisible;
    this.machineLimitsLoaded = true;
    this.machineLimitForm.markAsPristine();
    this.enableReadOnlyMode();
  }

  // Process individual limit group based on limitType
  private processLimitGroup(limitGroup: LimitGroup): void {
    limitGroup.entries.forEach(entry => {
      switch (entry.type) {
        case ENTRY_TYPES.MACHINE_MODES:
          this.setMachineModes(entry.stringValue, entry.stringArrayValue);
          break;
        case ENTRY_TYPES.USE_OF_MACHINERY:
          this.machineLimitForm.patchValue({ useOfMachinery: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.USER_ROLES:
          this.setUserRoles(entry.dataList);
          break;
        case ENTRY_TYPES.EXPOSURE_OF_OTHER_PERSON:
          this.machineLimitForm.patchValue({ exposure: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.NUMBER_OF_OPERATORS:
          this.machineLimitForm.patchValue({ numberOfOperators: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.OTHER_USER_LIMIT:
          this.machineLimitForm.patchValue({ otherUseLimit: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.MACHINE_SIZE:
          this.machineLimitForm.patchValue({ machineSize: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.HUMAN_INTERACTION:
          this.machineLimitForm.patchValue({ humanInteraction: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.POWER_SUPPLY_INTERFACE:
          this.machineLimitForm.patchValue({ powerSupplyInterface: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.OTHER_SPACE_LIMIT:
          this.machineLimitForm.patchValue({ otherSpaceLimit: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.LIFE_OF_MACHINERY:
          this.machineLimitForm.patchValue({ lifeOfMachinery: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.OTHER_TIME_LIMIT:
          this.machineLimitForm.patchValue({ otherTimeLimit: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.RECOMMENDED_SERVICE_INTERVALS:
          this.machineLimitForm.patchValue({ recommendedServiceInterval: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.PROPERTIES_OF_MATERIAL_TO_BE_PROCESSED:
          this.machineLimitForm.patchValue({ propertiesOfMaterialsToProcess: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.LEVEL_OF_CLEANLINESS:
          this.machineLimitForm.patchValue({ levelOfCleanliness: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.ENVIRONMENTAL_CONDITIONS:
          this.machineLimitForm.patchValue({ environmentalConditions: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.OTHER:
          this.machineLimitForm.patchValue({ other: entry.stringValue || '' });
          break;
        case ENTRY_TYPES.OTHER_LIMIT_FIELD_NAME_AND_DESCRIPTION:
          this.setOtherLimits(entry.dataList);
          break;
        case ENTRY_TYPES.PHASES_OF_MACHINE_LIFE_CYCLE:
          this.machineLimitForm.patchValue({ phaseOfMachineLifeCycle: entry.stringValue || '' });
          break;
        default:
          console.warn(`Unknown entry type: ${entry.type}`);
      }
    });
  }

  // Set machine modes from string array or stringArrayValue (handle both IDs and names)
  private setMachineModes(stringValue: string, stringArrayValue?: string[]): void {
    let modeData: string[] = [];
    
    if (stringArrayValue && Array.isArray(stringArrayValue)) {
      modeData = stringArrayValue;
    } else if (stringValue) {
      try {
        const parsed = JSON.parse(stringValue);
        modeData = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Error parsing machine modes:', error);
        return;
      }
    }
    
   
    // Check if data contains IDs or names
    const isUuidFormat = (value: string) => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(value);
    
    let modeIds: string[] = [];
    
    if (modeData.length > 0 && modeData.every(isUuidFormat)) {
      // Data contains IDs - use directly
      modeIds = modeData;
    } else {
      // Data contains names - convert to IDs
      const allModes = this.sharedValue?.machineModeList || [];
      
      modeIds = modeData.map(name => {
        // Try multiple matching strategies
        let mode = allModes.find(m => m.name === name);
        
        if (!mode) {
          mode = allModes.find(m => m.mode_name === name);
        }
        
        if (!mode) {
          // Try case-insensitive match
          mode = allModes.find(m => 
            m.name.toLowerCase() === name.toLowerCase() || 
            m.mode_name.toLowerCase() === name.toLowerCase()
          );
        }
        
        if (!mode) {
          // Try partial match (for similar words)
          mode = allModes.find(m => 
            m.name.toLowerCase().includes(name.toLowerCase()) || 
            name.toLowerCase().includes(m.name.toLowerCase()) ||
            m.mode_name.toLowerCase().includes(name.toLowerCase()) ||
            name.toLowerCase().includes(m.mode_name.toLowerCase())
          );
        }
        
        if (!mode) {
          console.warn(`⚠️ Could not find mode for "${name}". Available modes:`, allModes.map(m => `${m.name} / ${m.mode_name}`));
        }
        
        return mode?.id;
      }).filter(Boolean);
    }
    // Set the machine mode IDs in the form control
    this.machineLimitForm.patchValue({ machineMode: modeIds });
  }

  // Set user roles from dataList
  private setUserRoles(dataList: string | DataListItem[]): void {
    const userRolesArray = this.machineLimitForm.get('userRoles') as FormArray;
    let userRoles: DataListItem[] = [];

    if (typeof dataList === 'string') {
      try {
        userRoles = JSON.parse(dataList);
      } catch (error) {
        console.error('Error parsing user roles:', error);
        return;
      }
    } else if (Array.isArray(dataList)) {
      userRoles = dataList;
    }

    userRoles.forEach(userRole => {
      const userRoleGroup = this.formBuilder.group({
        userRole: [userRole.text || '', [this.characterLimitValidator(150)]],
        userDetails: [userRole.description || '', [this.characterLimitValidator(2000)]]
      });
      userRolesArray.push(userRoleGroup);
    });
  }

  // Set other limits from dataList
  private setOtherLimits(dataList: string | DataListItem[]): void {
    const otherLimitsArray = this.machineLimitForm.get('otherLimits') as FormArray;
    let otherLimits: DataListItem[] = [];

    if (typeof dataList === 'string') {
      try {
        otherLimits = JSON.parse(dataList);
      } catch (error) {
        return;
      }
    } else if (Array.isArray(dataList)) {
      otherLimits = dataList;
    }

    otherLimits.forEach(otherLimit => {
      const otherLimitGroup = this.formBuilder.group({
        otherLimitFieldName: [otherLimit.text || '', [this.characterLimitValidator(150)]],
        description: [otherLimit.description || '', [this.characterLimitValidator(2000)]]
      });
      otherLimitsArray.push(otherLimitGroup);
    });
  }

  // Method to get machine limits from API
  public getMachineLimits() {
    if (!this.machineId) {
      return;
    }

    this.machineLimitsLoaded = false;
    this.backendService.getMachineLimitsById(this.machineId).subscribe(
      (response: MachineLimitsApiResponse) => {
        if (response.statusCode === 200 && response.response) {
          this.loadMachineLimits(response.response);
          this.enableReadOnlyMode();
        }
      },
      (error) => {
        console.error('Error loading machine limits:', error);
      }
    );
  }

  // Common method to handle machine mode removal confirmation
  private async showRemoveMachineModeConfirmation(modeIds: string[], modesToRestore: string[], modeNames: string[]): Promise<boolean> {
    if (this.isCloneMode) {
      return true;
    }
    const props = {
      modeIds: modeIds,
      machineId: this.machineId,
      userId: this.userInfo?.userId,
      isDetach: false
    }

    // First check if there are hazards associated with these modes
    try {
      const hazardResponse = await this.checkHazardsForModes(props);
      
      // If no hazards found, allow direct removal and save
      if (!hazardResponse || !this.hasHazardData(hazardResponse)) {
        this.saveMachineLimits(); // Call save directly
        return true;
      }
      
      // Show popup with hazard data
      const popover = await this.modalController.create({
        component: RemoveMachineModeComponent,
        cssClass: 'mango-dashboard-customization-modal',
        componentProps: { 
          props: {
            modeIds: modeIds,
            modeNames: modeNames, // Add mode names for display
            machineId: this.machineId,
            userId: this.userInfo?.userId,
            hazardData: hazardResponse.response // Pass the hazard data directly
          }
        }
      });
      await popover.present();
      const { data } = await popover.onWillDismiss();
      
      if (data && data.removed) {
        // Call API with isDetach: true to actually remove the modes
        const detachProps = {
          modeIds: modeIds,
          machineId: this.machineId,
          userId: this.userInfo?.userId,
          isDetach: true
        };
        
        try {
          const detachResponse = await this.checkHazardsForModes(detachProps);
          
          // Check if detach was successful
          if (detachResponse && detachResponse.statusCode === 200) {
            this.saveMachineLimits(); // Call save after successful detach
            return true;
          } else {
            // Detach failed - show toast and restore modes
            const errorMessage = detachResponse?.message;
            this.toastService.presentToast(errorMessage, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
            this.machineLimitForm.get('machineMode').patchValue(modesToRestore);
            return false;
          }
        } catch (error) {
          console.error('Error detaching machine modes:', error);
          const errorMessage = error?.error?.message;
          this.toastService.presentToast(errorMessage, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
          this.machineLimitForm.get('machineMode').patchValue(modesToRestore);
          return false;
        }
      } else {
        // User cancelled - restore the modes
        this.machineLimitForm.get('machineMode').patchValue(modesToRestore);
        return false;
      }
      
    } catch (error) {
      const errorMessage = error?.error?.message || 'An error occurred while checking hazards. Please try again.';
      this.toastService.presentToast(errorMessage, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
      this.machineLimitForm.get('machineMode').patchValue(modesToRestore);
      return false;
    }
  }

  /**
   * Checks hazards for modes
   * 
   * @param data 
   * @returns hazards for modes 
   */
  private checkHazardsForModes(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.backendService.getHazardsMode(data).subscribe(
        (response: any) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  /**
   * Helper method to get mode names from IDs
   * @param modeIds - Array of mode IDs
   * @returns Array of mode names
   */
  private getModeNamesFromIds(modeIds: string[]): string[] {
    const allModes = this.sharedValue?.machineModeList || [];
    return modeIds.map(id => {
      const mode = allModes.find(m => m.id === id);
      return mode?.name || mode?.mode_name || id; // Fallback to ID if name not found
    });
  }

  /**
   * Determines whether hazard data has
   * 
   * @param response 
   * @returns true if hazard data 
   */
  private hasHazardData(response: any): boolean {
    if (!response || response.statusCode !== 200) {
      return false;
    }
    const hazardData = response.response;
    // Check if response has hazard data
    if (!hazardData) {
      return false;
    }
    // Example checks - modify based on your actual response structure
    if (Array.isArray(hazardData) && hazardData.length === 0) {
      return false;
    }
    
    if (typeof hazardData === 'object' && Object.keys(hazardData).length === 0) {
      return false;
    }
    return true; // Has hazard data
  }


  public async onRemoveMachineMode(event: any): Promise<void> {
    if (this.isCloneMode) {
      const remainingModes = this.machineLimitForm.get('machineMode').value || [];
      if (!remainingModes.length) {
        this.showOneMachineModeRequiredToast();
        this.machineLimitForm.get('machineMode').patchValue([event?.value?.id ?? event?.id ?? event]);
      }
      return;
    }
    const currentModes = this.machineLimitForm.get('machineMode').value as string[];
    const modeToRemove = event.value.id;
    // The dropdown has already removed the item, so we need to add it back to get the original count
    const originalModes = [...currentModes, modeToRemove];
    
    // Check if this would be removing the last mode (original count was 1)
    if (originalModes.length <= 1) {
      const requiredMessage = this.translate.instant('one_machine_mode_required');
      this.toastService.presentToast(requiredMessage, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
      // Restore the mode that was about to be removed
      const updatedModes = [...currentModes, modeToRemove];
      this.machineLimitForm.get('machineMode').patchValue(updatedModes);
      return;
    }
    
    // Proceed with normal removal flow if there are multiple modes
    const modeIdToRestore = event.value.id;
    const modeId = [event.value.id];
    const modeName = [event.value.name || event.value.mode_name]; // Get the name from the event
    const updatedModes = [...currentModes, modeIdToRestore];
    
    await this.showRemoveMachineModeConfirmation(modeId, updatedModes, modeName);
  }
 public async onClearAll(event): Promise<void> { 
    if (this.isCloneMode) {
      this.showOneMachineModeRequiredToast();
      this.machineLimitForm.get('machineMode').patchValue([...this.lastMachineModeIds]);
      return;
    }
    const requiredMessage = this.translate.instant('one_machine_mode_required');
    this.toastService.presentToast(requiredMessage, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
    const originalModeIds = [...this.selectedMachineModeIds];
    this.machineLimitForm.get('machineMode').patchValue(originalModeIds);
    //Commenting the below code for time being
    // // Store the current mode IDs - form expects IDs, not names
    // const originalModeIds = [...this.selectedMachineModeIds];
    // // Get the mode names for display
    // const modeNames = this.getModeNamesFromIds(originalModeIds);
    // // Pass the same IDs for both parameters since form stores and expects IDs
    // await this.showRemoveMachineModeConfirmation(originalModeIds, originalModeIds, modeNames);
  }

  private showOneMachineModeRequiredToast(): void {
    this.toastService.presentToast(this.translate.instant('one_machine_mode_required'), 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
  }
}
